import React, { useState, useRef, useImperativeHandle } from 'react';
import { Toggle } from '@core/uilibrary';
import ApiPost from '../../../../../../api-controller-mod/api-serv/post-api/postApi';
import SelectFieldInline from '../../../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectFieldInline';
import {
  IconCardSectionData, IconSectionWrapper, IconCardEditorHandle, MediaSaveCallback,
} from './types';
import { LabelRow, SplitRow, DraftTextField, DraftTextArea } from './primitives';
import ImageUploadField from './imageUploadField';
import { API_ENDPOINTS } from '../../../../../../context-controller-mod/context-serv/global-provider/api-shell/api-provider';
import Button from '../../../../../../component-controller-mod/component-serv/button-fs-cp/dynamic-button-op/ts/button';
import { AddNewCardIcon } from '../../../../../../asset-controller-mod/asset-serv/icons/iconExporter';

interface IconCardEditorProps {
  data:               IconSectionWrapper;
  cardLabel:          string;
  sectionKey:         string;
  onChange:           (v: IconSectionWrapper) => void;
  onMediaClick?:      (onSave: MediaSaveCallback) => void;
  userId:             string;
  registerMediaUsage: (uuid: string) => void;
}

const IconCardEditor = React.forwardRef<IconCardEditorHandle, IconCardEditorProps>((
  { data, cardLabel, onChange, onMediaClick, userId, registerMediaUsage }, ref,
) => {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [cardEdits,   setCardEdits]   = useState<Record<number, Partial<IconCardSectionData['list'][0]>>>({});

  const newCardIndices = useRef<number[]>([]);
  const cs             = data.content[0]?.cardSection;
  const orig           = useRef(cs);

  const setCs = (p: Partial<IconCardSectionData>) => onChange({
    ...data,
    content: [{ ...data.content[0], cardSection: { ...cs, ...p } }],
  });

  const setCardField = (field: keyof IconCardSectionData['list'][0], v: unknown) => {
    const updatedEdits = {
      ...cardEdits,
      [selectedIdx]: { ...(cardEdits[selectedIdx] ?? {}), [field]: v },
    };
    setCardEdits(updatedEdits);
    const updatedList = cs.list.map((item, i) => ({ ...item, ...(updatedEdits[i] ?? {}) }));
    setCs({ list: updatedList });
  };

  const handleAddCard = () => {
    const maxId   = cs.list.reduce((max, c) => Math.max(max, c.id ?? 0), 0);
    const newCard = { id: maxId + 1, title: '', iconAlt: '', iconSrc: '', description: '', visible: true };
    const updatedList = [...cs.list, newCard];
    const newIdx      = updatedList.length - 1;
    setCs({ list: updatedList });
    setSelectedIdx(newIdx);
    if (!newCardIndices.current.includes(newIdx)) {
      newCardIndices.current = [...newCardIndices.current, newIdx];
    }
  };

  useImperativeHandle(ref, () => ({
    getNewCards: () =>
      newCardIndices.current.map(idx => ({
        value: { ...cs.list[idx], ...(cardEdits[idx] ?? {}) } as IconCardSectionData['list'][0],
      })),
  }));

  if (!cs) return null;

  const rawCard    = cs.list[selectedIdx];
  const editedCard = rawCard ? { ...rawCard, ...(cardEdits[selectedIdx] ?? {}) } : undefined;
  const isNewCard  = newCardIndices.current.includes(selectedIdx);
  const origCard   = orig.current?.list[selectedIdx];
  const cardOptions = cs.list.map((c, i) => ({
    value: String(i),
    label: `Card${i + 1}/ ${c.title || 'New Card'}`,
  }));

  return (
    <>
      <LabelRow label="Main content title" />
      <SplitRow currentText={`[ ${orig.current?.label ?? ''} ]`}>
        <DraftTextField placeholder="Text title" onChange={v => setCs({ label: v })} />
      </SplitRow>

      <LabelRow label="Main title" />
      <SplitRow currentText={orig.current?.title ?? ''}>
        <DraftTextField placeholder="Text title" onChange={v => setCs({ title: v })} />
      </SplitRow>

      <LabelRow label="Title description" />
      <SplitRow currentText={orig.current?.description ?? ''} multiline>
        <DraftTextArea placeholder="Title description" rows={3}
          onChange={v => setCs({ description: v })} />
      </SplitRow>

      {/* ✅ cardSection.visible toggle */}
      <LabelRow
        label="Card Side visible"
        toggle={{
          checked:  cs.visible ?? true,
          onChange: (v: boolean) => setCs({ visible: v }),
        }}
      />

      <div className="scl--cms-content-edit-company-card-row">
        <span className="scl--cms-content-edit-company-label">{cardLabel}</span>
        <div className="scl--cms-content-edit-company-card-row-right">
          <SelectFieldInline
            className="scl--cms-content-edit-company-card-select"
            value={String(selectedIdx)}
            options={cardOptions}
            onChange={(v: string) => setSelectedIdx(Number(v))}
          />
          <Button variant='ghost' icon={<AddNewCardIcon />} onClick={handleAddCard} />
          {/* ✅ card-item visible toggle */}
          <Toggle
            checked={editedCard?.visible ?? true}
            onChange={(v: boolean) => setCardField('visible', v)}
          />
        </div>
      </div>
      <div className="scl--cms-content-edit-company-divider" />

      {editedCard && (
        <>
          <LabelRow label="Card title" />
          <SplitRow currentText={isNewCard ? '' : (origCard?.title ?? '')}>
            <DraftTextField
              key={`icon-title-${selectedIdx}`}
              initialValue={cardEdits[selectedIdx]?.title ?? ''}
              placeholder="Text title"
              onChange={v => setCardField('title', v)} />
          </SplitRow>

          <LabelRow label="Card description" />
          <SplitRow currentText={isNewCard ? '' : (origCard?.description ?? '')} multiline>
            <DraftTextArea
              key={`icon-desc-${selectedIdx}`}
              initialValue={cardEdits[selectedIdx]?.description ?? ''}
              placeholder="Card description" rows={3}
              onChange={v => setCardField('description', v)} />
          </SplitRow>

          <ImageUploadField
            key={`icon-upload-${selectedIdx}`}
            label="Icon"
            currentSrc={editedCard.iconSrc || undefined}
            onFile={async f => {
              try {
                const formData = new FormData();
                formData.append('file',        f);
                formData.append('type',        'IMAGE');
                formData.append('filename',    f.name);
                formData.append('alt',         editedCard.iconAlt ?? '');
                formData.append('description', '');

                const res           = await ApiPost(`${API_ENDPOINTS.UPLOAD_MEDIA}?userUuid=${userId}`, formData);
                const uploadedUrl:  string | undefined = (res as any)?.data?.data?.url;
                const uploadedUuid: string | undefined = (res as any)?.data?.data?.uuid;

                if (uploadedUrl) {
                  setCardField('iconSrc', uploadedUrl);
                  if (uploadedUuid) registerMediaUsage(uploadedUuid);
                  return uploadedUrl;
                }
              } catch (err) {
                console.error('[IconCardUpload] upload failed:', err);
              }
              return undefined;
            }}
            onMediaClick={
              onMediaClick
                ? () => onMediaClick(item => { if (item) setCardField('iconSrc', item.src); })
                : undefined
            }
          />
        </>
      )}
    </>
  );
});

export default IconCardEditor;